import { collection, doc, setDoc, Timestamp } from 'firebase/firestore';
import { db } from '../../../config';
import { ColumnMapping, MasterFilterKey, MasterSearchKey } from '../../../../../types/master';
import { buildLegacyMirror } from '../../../../../utils/master/matchKeys';

interface CreateMasterMappingParams {
  masterId: string;
  patternId: string;
  patternName: string;
  /** 検索キー（#3180）。照合の主体。1件のみ。 */
  searchKey: MasterSearchKey;
  /** 絞り込みキー（#3180）。AND 条件。未指定・空配列なら絞り込みなし。 */
  filterKeys?: MasterFilterKey[];
  /** 自動反映の下限スコア（#3263）。0〜100の整数(%)。未指定なら書かず、既定値に委ねる。 */
  autoApplyScore?: number;
  /** 要注意にする下限スコア（#3309）。0〜100の整数(%)。未指定なら書かず、既定値に委ねる。 */
  reviewThreshold?: number;
  /** AI照合リランク（#3355）。未指定は false として保存する。 */
  enableLlmRerank?: boolean;
  columnMappings: ColumnMapping[];
  // 紐づけ単位（取引先コードなど）の OCR 項目。任意。未指定は空文字で保存。
  scopeItemId?: string;
  scopeItemLabel?: string;
  /** パターン内での実行順（#3180）。未指定なら末尾扱いで保存しない。 */
  executionOrder?: number;
}

export const createMasterMapping = async (params: CreateMasterMappingParams): Promise<string> => {
  const mappingsRef = collection(db, 'masters', params.masterId, 'master_mappings');
  const newDocRef = doc(mappingsRef);
  const now = Timestamp.now();

  // 既存フィールドは search_key のミラーとして書き続ける（#3180）。
  // Cloud SQL の source_field_name 解決や再照合モーダル・結果画面のバッジが
  // search_item_id / search_item_label を直接参照しているため。
  const mirror = buildLegacyMirror(params.searchKey);

  await setDoc(newDocRef, {
    pattern_id: params.patternId,
    pattern_name: params.patternName,
    // 新形式（照合の正）
    search_key: params.searchKey,
    filter_keys: params.filterKeys ?? [],
    // 未設定なら書かない（既定値はバックエンドの定数が正のため、#3263）。
    ...(params.autoApplyScore !== undefined && { auto_apply_score: params.autoApplyScore }),
    ...(params.reviewThreshold !== undefined && { review_threshold: params.reviewThreshold }),
    // OFF も明示的に保存する（スコア系と違い、未設定と false を区別する必要がないため）。
    enable_llm_rerank: params.enableLlmRerank ?? false,
    // 旧形式（後方互換のミラー）
    search_item_id: mirror.searchItemId,
    search_item_label: mirror.searchItemLabel,
    search_csv_column: mirror.searchCsvColumn,
    match_mode: mirror.matchMode,
    column_mappings: params.columnMappings,
    scope_item_id: params.scopeItemId ?? '',
    scope_item_label: params.scopeItemLabel ?? '',
    ...(params.executionOrder !== undefined && { execution_order: params.executionOrder }),
    created_at: now,
    updated_at: now,
  });

  return newDocRef.id;
};
